import { Box, Typography, useTheme, useMediaQuery, ToggleButtonGroup, ToggleButton, IconButton, Tooltip } from "@mui/material";
import { tokens } from "../../theme";
import SpsDistributionChart from "../../components/charts/SpsDistributionChart";
import SpsDistributionHistoryChart from "../../components/charts/SpsDistributionHistoryChart";
import SpsBurnBarChart from "../../components/charts/SpsBurnBarChart";
import TokenStats from "../../components/display/TokenStats";
import DonateModal from "../../components/modals/DonateModal";
import LoginModal from "../../components/modals/LoginModal";
import TopSpsList from "../../components/display/TopSpsList";
import PremiumContent from "../../components/premium/PremiumContent";
import { useState, useEffect, useCallback, memo } from "react";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import Dialog from '@mui/material/Dialog';
import CloseIcon from '@mui/icons-material/Close';
import DiamondIcon from '@mui/icons-material/Diamond';

const Dashboard = memo(({ onDonationSuccess }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [donateModalOpen, setDonateModalOpen] = useState(false);
  const [chartViewMode, setChartViewMode] = useState("total");
  const [fullscreenChartOpen, setFullscreenChartOpen] = useState(false);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Media queries für responsive Design
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));

  // Handle donation modal - use callbacks to prevent recreation on render
  const handleDonateClick = useCallback(() => {
    setDonateModalOpen(true);
  }, []);

  const handleDonateModalClose = useCallback(() => {
    setDonateModalOpen(false);
  }, []);

  const handleDonateSuccess = useCallback((donationData) => {
    // Rufe die onDonationSuccess-Callback-Funktion auf, falls vorhanden
    if (onDonationSuccess) {
      onDonationSuccess(donationData);
    }
  }, [onDonationSuccess]);

  // Event-Listener für "openLoginModal"-Event und openDonateModal
  useEffect(() => {
    const handleOpenLoginModal = () => {
      setLoginModalOpen(true);
    };

    const handleOpenDonateModal = () => {
      setDonateModalOpen(true);
    };

    // Event-Listener registrieren
    document.addEventListener('openLoginModal', handleOpenLoginModal);
    document.addEventListener('openDonateModal', handleOpenDonateModal);

    // Event-Listener entfernen beim Unmount
    return () => {
      document.removeEventListener('openLoginModal', handleOpenLoginModal);
      document.removeEventListener('openDonateModal', handleOpenDonateModal);
    };
  }, []);

  // Handle chart view mode change
  const handleViewModeChange = useCallback((event, newMode) => {
    if (newMode !== null) {
      setChartViewMode(newMode);
    }
  }, []);

  // Handle fullscreen chart
  const handleFullscreenOpen = useCallback(() => {
    setFullscreenChartOpen(true);
  }, []);

  const handleFullscreenClose = useCallback(() => {
    setFullscreenChartOpen(false);
  }, []);

  return (
    <Box m={isMobile ? "10px" : "20px"}>
      {/* HEADER */}
      <Box 
        display="flex" 
        flexDirection={isMobile ? "column" : "row"} 
        justifyContent="space-between" 
        alignItems={isMobile ? "flex-start" : "center"}
        mb={isMobile ? 2 : 0}
      >
      </Box>


      {/* GRID & CHARTS */}
      <Box
        display="grid"
        gridTemplateColumns={
          isMobile ? "1fr" : 
          isTablet ? "repeat(6, 1fr)" : 
          "repeat(12, 1fr)"
        }
        gridAutoRows={isMobile ? "auto" : "140px"}
        gap={isMobile ? "15px" : "20px"}
      >
        {/* Rest of the dashboard remains the same */}
        
        {/* ROW 1 - SPS Distribution Chart and Stats */}
        <Box
          gridColumn={
            isMobile ? "span 1" : 
            isTablet ? "span 6" : 
            "span 6"
          }
          gridRow={isMobile ? "span 3" : "span 3"}
          backgroundColor={colors.primary[400]}
          p={isMobile ? "15px" : "30px"}
          pb={isMobile ? "40px" : "60px"}
          position="relative"
        >
          <Box 
            display="flex" 
            justifyContent="space-between" 
            alignItems="center" 
            mb="20px"
          >
            <Box display="flex" alignItems="center">
              <Typography variant="h5" fontWeight="600" sx={{ marginBottom: "5px" }}>
                SPS Token Distribution
              </Typography>
              <Tooltip title="Premium Feature" arrow>
                <DiamondIcon sx={{ ml: 1, fontSize: 18, color: colors.blueAccent[400] }} />
              </Tooltip>
            </Box>
            <Box display="flex" alignItems="center">
              <IconButton 
                onClick={handleFullscreenOpen} 
                size="small" 
                sx={{ mr: 1 }}
              >
                <FullscreenIcon sx={{ color: colors.grey[100] }} />
              </IconButton>
              <ToggleButtonGroup
                value={chartViewMode}
                exclusive
                onChange={handleViewModeChange}
                aria-label="View Mode"
                size="small"
                color="secondary"
              >
                <ToggleButton value="total" aria-label="Total Distribution">
                  Total
                </ToggleButton>
                <ToggleButton value="reward_pools" aria-label="Reward Pools">
                  Rewards
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          <Box height={isMobile ? "300px" : "400px"}>
            <PremiumContent 
              featureId="SPS_DISTRIBUTION_CHART"
              height="90%"
              blurIntensity={5}
            >
              <SpsDistributionChart viewMode={chartViewMode} />
            </PremiumContent>
          </Box>
        </Box>
        
        <Box
          gridColumn={
            isMobile ? "span 1" :
            isTablet ? "span 3" :
            "span 3"
          }
          gridRow={
            isMobile ? "span 3" :
            "span 3"
          }
          backgroundColor={colors.primary[400]}
          p={isMobile ? "15px" : "30px"}
        >
          <Typography variant="h5" fontWeight="600" sx={{ marginBottom: "15px" }}>
            SPS Token Stats
          </Typography>
          <TokenStats />
        </Box>

        {/* Auto Conversion Status & Top SPS List in a container */}
        <Box
          gridColumn={
            isMobile ? "span 1" :
            isTablet ? "span 3" :
            "span 3"
          }
          gridRow={
            isMobile ? "span 6" :
            "span 3"
          }
          backgroundColor={colors.primary[400]}
          p={isMobile ? "15px" : "20px"}
          display="flex"
          flexDirection="column"
        >
          {/* Top SPS Holder */}
          <Box mb={3} flex="1">
            <Box display="flex" alignItems="center" mb={2}>
              <Typography variant="h5" fontWeight="600">
                Top SPS Holders
              </Typography>
              <Tooltip title="Premium Feature" arrow>
                <DiamondIcon sx={{ ml: 1, fontSize: 18, color: colors.blueAccent[400] }} />
              </Tooltip>
            </Box>
            <PremiumContent 
              featureId="SPS_TOP_LIST"
              height="90%"
              blurIntensity={5}
            >
              <TopSpsList />
            </PremiumContent>
          </Box>
        </Box>
        
        {/* SPS Burn Bar Chart */}
        <Box
          gridColumn={
            isMobile ? "span 1" :
            "span 12"
          }
          gridRow={
            isMobile ? "span 3" :
            "span 3"
          }
          backgroundColor={colors.primary[400]}
          p={isMobile ? "15px" : "30px"}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="h5" fontWeight="600">
              SPS Burn History
            </Typography>
            <Tooltip title="Premium Feature" arrow>
              <DiamondIcon sx={{ ml: 1, fontSize: 18, color: colors.blueAccent[400] }} />
            </Tooltip>
          </Box>
          <PremiumContent 
            featureId="SPS_BURN_CHART"
            height="90%"
            blurIntensity={5}
          >
            <SpsBurnBarChart />
          </PremiumContent>
        </Box>

        {/* SPS Distribution History Chart */}
        <Box
          gridColumn={
            isMobile ? "span 1" :
            "span 12"
          }
          gridRow={
            isMobile ? "span 4" : 
            "span 4"
          }
          backgroundColor={colors.primary[400]}
          p={isMobile ? "15px" : "30px"}
        >
          <Box display="flex" alignItems="center" mb={2}>
            <Typography variant="h5" fontWeight="600">
              SPS Distribution History
            </Typography>
            <Tooltip title="Premium Feature" arrow>
              <DiamondIcon sx={{ ml: 1, fontSize: 18, color: colors.blueAccent[400] }} />
            </Tooltip>
          </Box>
          <PremiumContent 
            featureId="SPS_DISTRIBUTION_HISTORY"
            height="100%"
            blurIntensity={5}
          >
            <SpsDistributionHistoryChart days={30} hideRewards={true} />
          </PremiumContent>
        </Box>
      </Box>

      {/* Modals */}
      <DonateModal
        open={donateModalOpen}
        onClose={handleDonateModalClose}
        onSuccess={handleDonateSuccess}
      />
      
      {/* Fullscreen Chart Dialog */}
      <Dialog
        open={fullscreenChartOpen}
        onClose={handleFullscreenClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            height: '80vh',
          }
        }}
      >
        <Box sx={{ 
          position: "relative", 
          height: "100%", 
          width: "100%", 
          display: "flex",
          flexDirection: "column",
          backgroundColor: colors.primary[400],
          padding: "20px" 
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5" fontWeight="600">
              SPS Token Distribution {chartViewMode === "reward_pools" ? "- Reward Pools" : "- Total"}
            </Typography>
            <IconButton onClick={handleFullscreenClose} aria-label="Close">
              <CloseIcon sx={{ color: colors.grey[100] }} />
            </IconButton>
          </Box>
          <Box sx={{ flexGrow: 1, position: "relative", minHeight: "calc(80vh - 80px)" }}>
            <PremiumContent 
              featureId="SPS_DISTRIBUTION_CHART"
              height="100%"
              blurIntensity={5}
            >
              <SpsDistributionChart viewMode={chartViewMode} isFullscreen={true} />
            </PremiumContent>
          </Box>
        </Box>
      </Dialog>

      {/* Login Modal */}
      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
      />
    </Box>
  );
});

export default Dashboard;
